import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CKEditor } from 'ckeditor4-react';
import { FrameDataType } from '@/modules/types/frame';
import { useParams } from 'react-router-dom';
import { ElearningParamType } from '@/pages/Module/routes';
import { getAssetLocation, getCkEditorConfig, hasVocamData } from '@/lib/util';
import VideoPlayerHls from '@/components/VideoPlayerHls';
import FrameTooltips from '@/components/Player/FrameContent/FrameTooltips';
import { ElearningBehaviourSettingsDataInterface } from '@/modules/types/tenant';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import appConfig from '@/lib/config';

interface CanvasProps {
  frameContent?: FrameDataType;
  onChange: (e: any) => void;
  onLoad: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  isMobile: boolean;
  vocamVideoLink?: string;
  elearningBehaviourSettings: ElearningBehaviourSettingsDataInterface;
}

const Canvas = ({
  frameContent,
  onChange,
  onLoad,
  isMobile,
  onFocus,
  onBlur,
  vocamVideoLink,
  elearningBehaviourSettings,
}: CanvasProps): ReactElement => {
  const dispatch = useDispatch();
  const [editor, setEditor] = useState<any>(null);
  const contentData = isMobile
    ? frameContent?.mobileContent || ''
    : frameContent?.content || '';

  const { elearningId } = useParams<ElearningParamType>();

  // Helper function to extract hash from Images resource type
  const extractImagesHash = (data: any): string | null =>
    data?.resourceTypes?.find((rt: any) => rt.name === 'Images')?.hash ?? null;

  // Function to fetch hash from CKFinder connector Init command
  const fetchHashFromConnector = async (id: string): Promise<string | null> => {
    const connectorUrl = getCkEditorConfig({
      tenantId: elearningBehaviourSettings.tenantId,
      type: 'elearnings',
      elearningId: id,
    }).filebrowserImageUploadUrl;

    // Parse the connector URL to get base path
    const url = new URL(connectorUrl, window.location.origin);
    const initUrl = `${url.pathname}?command=Init&${url.search.substring(1)}`;

    try {
      const response = await fetch(initUrl);
      const data = await response.json();
      return extractImagesHash(data);
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: 'Failed to initialize image upload. Please try again.',
        },
      });
      console.error('Error fetching CKFinder hash:', error);
      return null;
    }
  };

  const onInstanceReady = (e: any) => {
    const editorInstance = e.editor;

    // Prevent all notifications for file uploads
    editorInstance.on('notificationShow', (event: any) => {
      const { notification } = event.data;
      if (
        notification.message &&
        (notification.message.toLowerCase().includes('upload') ||
          notification.message.toLowerCase().includes('file') ||
          notification.message.toLowerCase().includes('image'))
      ) {
        event.cancel();
      }
    });

    // hack fix paste in chrome browser - similar to the one in global config.js
    editorInstance.on('beforeCommandExec', (event: any) => {
      // Show the paste dialog for the paste buttons and right-click paste
      if (event.data.name === 'paste') {
        event.editor._.forcePasteDialog = true;
      }
      // Don't show the paste dialog for Ctrl+Shift+V
      if (
        event.data.name === 'pastetext' &&
        event.data.commandData.from === 'keystrokeHandler'
      ) {
        event.cancel();
      }
    });

    // Cache for CKFinder hash (fetched from connector Init command)
    let imagesResourceHash: string | null = null;
    let hashFetchPromise: Promise<string | null> | null = null;

    // Function to fetch hash from CKFinder connector Init command
    const fetchImagesResourceHash = async (): Promise<string | null> => {
      if (hashFetchPromise) {
        return hashFetchPromise;
      }

      hashFetchPromise = fetchHashFromConnector(elearningId);
      return hashFetchPromise;
    };

    // Fix image upload URL to include CKFinder required parameters - somehow command and type parameters are not being set correctly
    // Use priority 1 to run as early as possible, before any other handlers
    editorInstance.on(
      'fileUploadRequest',
      (event: any) => {
        const { fileLoader } = event.data;
        if (!fileLoader) {
          return;
        }

        const isImageUpload = fileLoader.file?.type.startsWith('image/');
        if (!isImageUpload) {
          return;
        }

        let { uploadUrl } = fileLoader;
        if (!uploadUrl) {
          const config = getCkEditorConfig({
            tenantId: elearningBehaviourSettings.tenantId,
            type: 'elearnings',
            elearningId,
          });
          uploadUrl = config.filebrowserImageUploadUrl;
          fileLoader.uploadUrl = uploadUrl;
        }

        let url: URL;
        try {
          url = new URL(uploadUrl);
        } catch {
          url = new URL(uploadUrl, window.location.origin);
        }

        // Preserve existing query parameters (basedir, path, etc.) and add required CKFinder parameters
        // These parameters are required for CKFinder FileUpload command
        url.searchParams.set('command', 'FileUpload');
        url.searchParams.set('type', 'Images');
        url.searchParams.set('lang', 'en');
        url.searchParams.set('langCode', 'en');
        url.searchParams.set('currentFolder', '/');
        url.searchParams.set('responseType', 'json');

        if (!url.searchParams.has('hash')) {
          if (imagesResourceHash) {
            url.searchParams.set('hash', imagesResourceHash);
          } else {
            fetchImagesResourceHash().then((hash) => {
              if (hash) {
                imagesResourceHash = hash;
              }
            });
          }
        }

        // Reconstruct the URL synchronously
        const originalWasAbsolute =
          uploadUrl.startsWith('http://') || uploadUrl.startsWith('https://');
        const modifiedUrl = originalWasAbsolute
          ? url.toString()
          : url.pathname + url.search;

        // Store the modified URL in multiple places to ensure CKEditor uses it
        fileLoader.uploadUrl = modifiedUrl;
      },
      null,
      null,
      1,
    );

    // Handle file upload response to extract and format the image URL to use for img tag src attribute
    editorInstance.on('fileUploadResponse', (event: any) => {
      const { fileLoader } = event.data;
      if (!fileLoader) {
        return;
      }

      // Check if this is an image upload
      const isImageUpload = fileLoader.file?.type.startsWith('image/');
      if (!isImageUpload) {
        return;
      }

      // check for upload response
      const { xhr } = fileLoader;
      if (!xhr?.responseText) {
        return;
      }

      // Check if response is valid JSON (not HTML error page)
      const responseText = xhr.responseText.trim();
      if (
        !responseText ||
        responseText.startsWith('<!DOCTYPE') ||
        responseText.startsWith('<html')
      ) {
        return;
      }

      // Check HTTP status code
      if (xhr.status < 200 || xhr.status >= 300) {
        return;
      }

      const response = JSON.parse(responseText);

      let assetUrl: string | undefined;
      let fileName: string | undefined;

      if (response.fileName) {
        fileName = response.fileName;
      } else if (response.url) {
        const urlParts = response.url.split('/');
        [fileName] = urlParts.slice(-1);
      } else if (response.path) {
        const pathParts = response.path.split('/');
        [fileName] = pathParts.slice(-1);
      }

      // If we have a filename but no URL yet, construct it using getAssetLocation
      if (fileName && !assetUrl) {
        // Remove any query parameters and fragments from filename
        const [nameWithoutQuery] = fileName.split('?');
        const [cleanFileName] = nameWithoutQuery.split('#');
        fileName = cleanFileName;

        assetUrl = getAssetLocation({
          tenantId: elearningBehaviourSettings.tenantId,
          entityId: elearningId,
          entity: 'elearnings',
          fileType: 'images',
          fileName,
        });
      }

      // Set the URL in the response data so CKEditor uses it
      if (assetUrl && fileName) {
        // Remove any query parameters and fragments from filename
        const [nameWithoutQuery] = fileName.split('?');
        const [cleanFileName] = nameWithoutQuery.split('#');

        event.data.url = assetUrl;
        event.data.fileName = cleanFileName;

        // Also set it directly on fileLoader
        fileLoader.url = assetUrl;
        fileLoader.fileName = cleanFileName;
      }
    });
  };

  const onLoaded = (e: any) => {
    const editorInstance = e.editor;
    setEditor(editorInstance);

    // Fallback: If onInstanceReady doesn't fire for inline editors,
    // manually listen for instanceReady event
    if (editorInstance) {
      if (editorInstance.status === 'ready') {
        onInstanceReady({ editor: editorInstance });
      } else {
        editorInstance.once('instanceReady', () => {
          onInstanceReady({ editor: editorInstance });
        });
      }
    }
  };

  useEffect(() => {
    if (editor) {
      editor.setData(contentData);
    }
  }, [editor]);

  if (!elearningBehaviourSettings || !elearningBehaviourSettings.tenantId) {
    return <div> Some thing is wrong please try again later</div>;
  }

  const hasAllVocamData = hasVocamData(frameContent);

  return (
    <>
      <div className="module-viewer-style" onFocus={onFocus} onBlur={onBlur}>
        <div className="canvas-content">
          {frameContent?.isVideoFrame &&
            (!!frameContent?.bgVideo ||
              (hasAllVocamData &&
                elearningBehaviourSettings.videoLibraryEnabled)) && (
              <VideoPlayerHls
                videoLink={
                  hasAllVocamData && vocamVideoLink
                    ? vocamVideoLink
                    : getAssetLocation({
                        tenantId: frameContent.tenantId,
                        entity: 'elearnings',
                        entityId: frameContent.elearningId,
                        fileType: 'videos',
                        fileName: `${frameContent.bgVideo}`,
                      })
                }
                useHls={hasAllVocamData}
              />
            )}
          {frameContent?.isVideoFrame &&
            !frameContent?.bgVideo &&
            hasAllVocamData &&
            !elearningBehaviourSettings.videoLibraryEnabled && (
              <div className="video-library-message">
                <p>
                  Your Video Library license has been disabled. Please, contact
                  your Account Manager to re-enable this
                </p>
              </div>
            )}
          {!frameContent?.isVideoFrame && !hasAllVocamData && (
            <CKEditor
              id="frame-textarea" /* TO-DO: it doesn't get applied. will need to find a way to set height by inline css as CB does */
              editorUrl={appConfig.ckEditorScriptUrl}
              initData={contentData}
              type="inline"
              onChange={onChange}
              onDataReady={onLoad}
              onLoaded={onLoaded}
              onInstanceReady={onInstanceReady}
              config={getCkEditorConfig({
                tenantId: elearningBehaviourSettings.tenantId,
                type: 'elearnings',
                elearningId,
              })}
            />
          )}
        </div>
      </div>
      {!frameContent?.isVideoFrame &&
        (frameContent?.elements?.length ?? 0) > 0 && (
          <FrameTooltips
            elements={frameContent?.elements ?? []}
            isMobileView={isMobile}
          />
        )}
    </>
  );
};

export default Canvas;
